/*
  HandwritingUploadsPanel — Stage 1 of the Handwriting Recognition
  specification. Rendered inside a client's Health Record tab.

  Practitioners upload PDFs / photos of handwritten intake forms and
  assessment notes; the backend calls the configured vision LLM
  (default: openai/gpt-5.6-luna) and returns structured blocks; this
  panel then opens the review dialog side-by-side so the reviewer can
  edit / confirm / mark illegible before approving.

  Clinical-safety rules baked in via the API:
  - Sensitive-block gate blocks approval until every sensitive block
    is reviewed.
  - Original bytes are always preserved.
  - Approving mirrors the file into ``patient_attachments`` so the
    timeline picks it up.
*/
import { useCallback, useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Upload, Trash2, RefreshCw, ScanLine, AlertTriangle, Camera, Plus, X } from "lucide-react";
import HandwritingReviewDialog from "./HandwritingReviewDialog";

const STATUS_TONE = {
  uploaded:            "bg-zinc-100 text-zinc-700 border-zinc-300",
  processing:          "bg-blue-50 text-blue-800 border-blue-300",
  ready_for_review:    "bg-amber-50 text-amber-800 border-amber-300",
  requires_attention:  "bg-orange-50 text-orange-800 border-orange-300",
  approved:            "bg-emerald-50 text-emerald-800 border-emerald-300",
  failed:              "bg-red-50 text-red-800 border-red-300",
  archived:            "bg-zinc-100 text-zinc-500 border-zinc-300",
};

const STATUS_LABEL = {
  uploaded: "Queued",
  processing: "Processing…",
  ready_for_review: "Ready to review",
  requires_attention: "Needs review",
  approved: "Approved",
  failed: "Failed",
  archived: "Archived",
};

const POLL_MS = 2500;
const POLL_STATUSES = new Set(["uploaded", "processing"]);

export default function HandwritingUploadsPanel({ email, onApproved }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [openId, setOpenId] = useState(null);
  // Client-side buffer of captured/attached page images before they
  // are POSTed as a single multi-file document. Populated by the
  // "Take photo" flow (mobile camera) or by dragging additional
  // files into the tray from desktop.
  const [tray, setTray] = useState([]);       // [{file, previewUrl, id}]
  const fileInput = useRef(null);
  const cameraInput = useRef(null);
  const pollTimer = useRef(null);

  const encoded = encodeURIComponent(email || "");

  const load = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    try {
      const r = await api.get(`/health-record/${encoded}/handwriting/uploads`);
      setRows(r.data?.uploads || []);
    } catch (e) {
      toast.error("Couldn't load handwriting uploads", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  }, [email, encoded]);

  useEffect(() => { load(); }, [load]);

  // Poll while any row is still processing so the UI doesn't feel stuck.
  useEffect(() => {
    if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
    const anyActive = rows.some((r) => POLL_STATUSES.has(r.status));
    if (anyActive) {
      pollTimer.current = setInterval(() => {
        load();
      }, POLL_MS);
    }
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [rows, load]);

  function addToTray(files) {
    if (!files || files.length === 0) return;
    const additions = Array.from(files).map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      previewUrl: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
      name: f.name,
    }));
    setTray((t) => [...t, ...additions]);
  }

  function removeFromTray(id) {
    setTray((t) => {
      const next = t.filter((x) => x.id !== id);
      const removed = t.find((x) => x.id === id);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return next;
    });
  }

  function clearTray() {
    for (const item of tray) if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    setTray([]);
  }

  async function uploadTray() {
    if (tray.length === 0) return;
    setUploading(true);
    try {
      const form = new FormData();
      for (const t of tray) form.append("files", t.file);
      form.append("classification", "intake");
      form.append("auto_process", "true");
      await api.post(
        `/health-record/${encoded}/handwriting/uploads`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(
        tray.length === 1
          ? "Document uploaded — extracting…"
          : `${tray.length} pages uploaded as one document — extracting…`,
      );
      clearTray();
      await load();
    } catch (e) {
      toast.error("Upload failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
      if (cameraInput.current) cameraInput.current.value = "";
    }
  }

  // Legacy shortcut for the file input's onChange — either sends
  // immediately (empty tray) or appends to the tray (multi-page).
  async function handleFilePicker(files) {
    if (!files || files.length === 0) return;
    if (tray.length === 0 && files.length === 1) {
      // Single-file quick upload — skip the tray so it stays snappy
      // for the common desktop case of "pick one PDF, done".
      setUploading(true);
      try {
        const form = new FormData();
        form.append("files", files[0]);
        form.append("classification", "intake");
        form.append("auto_process", "true");
        await api.post(
          `/health-record/${encoded}/handwriting/uploads`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        toast.success("Document uploaded — extracting…");
        await load();
      } catch (e) {
        toast.error("Upload failed", {
          description: e?.response?.data?.detail || e.message,
        });
      } finally {
        setUploading(false);
        if (fileInput.current) fileInput.current.value = "";
      }
    } else {
      addToTray(files);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function rerun(id) {
    try {
      await api.post(`/handwriting/uploads/${id}/process`);
      toast.info("Re-running extraction…");
      await load();
    } catch (e) {
      toast.error("Couldn't re-process", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function remove(id) {
    if (!confirm("Delete this upload? The original file remains in storage.")) return;
    try {
      await api.delete(`/handwriting/uploads/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <Card
      className="p-4 rounded-lg border border-zinc-200 shadow-none"
      data-testid="handwriting-uploads-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ScanLine className="h-4 w-4 text-indigo-700" />
          <h3 className="font-medium text-sm">Handwriting uploads</h3>
          <span className="text-xs text-zinc-500">
            Scan or photograph handwritten intake forms & assessment notes — the AI transcribes for review.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => cameraInput.current?.click()}
            disabled={uploading}
            data-testid="hw-camera-btn"
            title="Take a photo with the device camera"
          >
            <Camera className="h-3.5 w-3.5 mr-1" />
            {tray.length > 0 ? "Add page" : "Take photo"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            data-testid="hw-upload-btn"
          >
            {uploading
              ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Uploading…</>
              : <><Upload className="h-3.5 w-3.5 mr-1" /> Upload document</>
            }
          </Button>
          <input
            ref={fileInput}
            type="file"
            multiple
            accept="application/pdf,image/png,image/jpeg,image/webp,image/heic,image/heif"
            className="hidden"
            onChange={(e) => handleFilePicker(e.target.files)}
            data-testid="hw-upload-input"
          />
          <input
            ref={cameraInput}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => addToTray(e.target.files)}
            data-testid="hw-camera-input"
          />
        </div>
      </div>

      {/* Batch capture tray — appears when the practitioner has photos
          queued but not yet uploaded. Lets them keep adding pages
          before firing a single multi-file upload. */}
      {tray.length > 0 && (
        <div
          className="mb-3 border border-indigo-200 bg-indigo-50/40 rounded-md p-3"
          data-testid="hw-capture-tray"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-medium text-indigo-900 flex items-center gap-2">
              <Camera className="h-3.5 w-3.5" />
              {tray.length} page{tray.length === 1 ? "" : "s"} ready to upload as one document
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => cameraInput.current?.click()}
                disabled={uploading}
                data-testid="hw-tray-add-page"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add another page
              </Button>
              <Button
                size="sm"
                onClick={uploadTray}
                disabled={uploading}
                data-testid="hw-tray-upload"
              >
                {uploading
                  ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Uploading…</>
                  : <><Upload className="h-3.5 w-3.5 mr-1" /> Upload {tray.length} page{tray.length === 1 ? "" : "s"}</>
                }
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={clearTray}
                disabled={uploading}
                data-testid="hw-tray-clear"
                title="Discard captured pages"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {tray.map((t, i) => (
              <div
                key={t.id}
                className="relative w-20 h-24 border border-zinc-200 bg-white rounded overflow-hidden shrink-0"
                data-testid={`hw-tray-item-${t.id}`}
              >
                {t.previewUrl ? (
                  <img src={t.previewUrl} alt={t.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-2xs text-zinc-500 flex items-center justify-center h-full text-center px-1">
                    {t.name}
                  </div>
                )}
                <div className="absolute top-0 left-0 bg-black/60 text-white text-2xs px-1">
                  {i + 1}
                </div>
                <button
                  type="button"
                  className="absolute top-0 right-0 bg-red-600 text-white text-2xs p-0.5 rounded-bl"
                  onClick={() => removeFromTray(t.id)}
                  disabled={uploading}
                  data-testid={`hw-tray-remove-${t.id}`}
                  title="Remove this page"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div className="text-sm text-zinc-500 py-6 text-center">
          <Loader2 className="h-4 w-4 inline mr-1 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-zinc-500 py-6 text-center">
          Nothing uploaded yet. Drop a PDF or photograph to get started.
        </div>
      ) : (
        <div className="divide-y divide-zinc-200">
          {rows.map((r) => (
            <div
              key={r.id}
              className="py-2.5 flex items-center gap-3"
              data-testid={`hw-row-${r.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-zinc-900 truncate flex items-center gap-2">
                  {r.filename}
                  {r.pending_sensitive_count > 0 && (
                    <span
                      className="inline-flex items-center gap-1 text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200"
                      title={`${r.pending_sensitive_count} sensitive field(s) still need review`}
                    >
                      <AlertTriangle className="h-3 w-3" />
                      {r.pending_sensitive_count} sensitive
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {r.classification} · {r.block_count ?? 0} extracted blocks
                  {r.page_count ? ` · ${r.page_count} page${r.page_count === 1 ? "" : "s"}` : ""}
                  {r.model ? ` · ${r.model}` : ""}
                </div>
                {r.processing?.error && (
                  <div className="text-xs text-red-700 mt-0.5">Error: {r.processing.error}</div>
                )}
              </div>
              <Badge className={`text-2xs uppercase border ${STATUS_TONE[r.status] || STATUS_TONE.uploaded}`}>
                {STATUS_LABEL[r.status] || r.status}
              </Badge>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setOpenId(r.id)}
                  disabled={r.status === "processing"}
                  data-testid={`hw-review-${r.id}`}
                >
                  {r.status === "approved" ? "View" : "Review"}
                </Button>
                {r.status !== "approved" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => rerun(r.id)}
                    title="Re-run AI extraction"
                    disabled={r.status === "processing"}
                    data-testid={`hw-rerun-${r.id}`}
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => remove(r.id)}
                  title="Archive upload"
                  data-testid={`hw-delete-${r.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5 text-red-600" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {openId && (
        <HandwritingReviewDialog
          uploadId={openId}
          open={!!openId}
          onOpenChange={(v) => { if (!v) { setOpenId(null); load(); onApproved?.(); } }}
          apiBase={API}
        />
      )}
    </Card>
  );
}
